/**
 * Authenticated API Helper
 * 
 * Provides a unified fetch wrapper that automatically attaches Bearer tokens
 * for authenticated requests. Works with both Supabase and local auth.
 * 
 * This helper should be used for ALL branding/logo API calls to ensure
 * consistent authentication on preview domains.
 */

import { getClient } from './providers/supabaseAuth';
import { API_URL } from './authConfig';

/**
 * Get the current authentication token
 * Checks Supabase session first, then falls back to localStorage
 * @returns {Promise<string|null>} The auth token or null if not authenticated
 */
export async function getAuthToken() {
  // P0 FIX: Check localStorage token FIRST (set by email/password login
  // or by Supabase sync). This prevents a stale Supabase session for a
  // different Google account from being used instead of the active login.
  const localToken = localStorage.getItem('auth_token');
  if (localToken) {
    return localToken;
  }

  // Fall back to Supabase session (only if no local token exists)
  const supabase = getClient();
  if (supabase) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        return session.access_token;
      }
    } catch (e) {
      console.warn('[AuthAPI] Supabase session check failed:', e);
    }
  }
  
  return null;
}

/**
 * Create headers with Bearer token authentication
 * @param {Object} additionalHeaders - Additional headers to include
 * @returns {Promise<Headers>} Headers object with Authorization
 */
export async function createAuthHeaders(additionalHeaders = {}) {
  const token = await getAuthToken();
  const headers = new Headers(additionalHeaders);
  
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  return headers;
}

/**
 * Authenticated fetch wrapper
 * Automatically attaches Bearer token to all requests
 * 
 * @param {string} endpoint - API endpoint (with or without /api prefix)
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 * 
 * @example
 * // Simple GET
 * const response = await authFetch('/branding/diagnostics');
 * 
 * @example
 * // POST with JSON
 * const response = await authFetch('/brand-kit', {
 *   method: 'POST',
 *   body: JSON.stringify({ primary_color: '#FF6600' })
 * });
 * 
 * @example
 * // File upload (FormData)
 * const formData = new FormData();
 * formData.append('file', file);
 * const response = await authFetch('/tenants/logo/upload', {
 *   method: 'POST',
 *   body: formData,
 *   // Don't set Content-Type for FormData - browser will set it with boundary
 *   skipContentType: true
 * });
 */
export async function authFetch(endpoint, options = {}) {
  const token = await getAuthToken();
  
  // Normalize endpoint - ensure it starts with /api
  let url = endpoint;
  if (!url.startsWith('http')) {
    if (!url.startsWith('/api')) {
      url = `/api${url.startsWith('/') ? url : '/' + url}`;
    }
    url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  
  // Build headers
  const headers = new Headers(options.headers || {});
  
  // Add Authorization header
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Add Content-Type for JSON (unless skipContentType is true or body is FormData)
  const isFormData = options.body instanceof FormData;
  if (!options.skipContentType && !isFormData && options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  
  // Build final options
  const fetchOptions = {
    ...options,
    headers,
    credentials: 'include', // Always include cookies as fallback
  };
  
  // Remove custom options that aren't valid fetch options
  delete fetchOptions.skipContentType;
  
  return fetch(url, fetchOptions);
}

/**
 * Authenticated JSON fetch - returns parsed JSON
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Parsed JSON response
 * @throws {Error} If response is not OK
 */
export async function authFetchJson(endpoint, options = {}) {
  const response = await authFetch(endpoint, options);
  
  if (!response.ok) {
    let errorDetail = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorDetail = errorData.detail || errorData.message || errorDetail;
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(errorDetail);
  }
  
  return response.json();
}

/**
 * Upload a file with authentication
 * @param {string} endpoint - API endpoint
 * @param {File} file - File to upload
 * @param {string} fieldName - Form field name (default: 'file')
 * @returns {Promise<Object>} Parsed JSON response
 */
export async function authUploadFile(endpoint, file, fieldName = 'file') {
  const formData = new FormData();
  formData.append(fieldName, file);
  
  const response = await authFetch(endpoint, {
    method: 'POST',
    body: formData,
    skipContentType: true, // Let browser set multipart boundary
  });
  
  if (!response.ok) {
    let errorDetail = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorDetail = errorData.detail || errorData.message || errorDetail;
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(errorDetail);
  }
  
  return response.json();
}

/**
 * Check if user is authenticated by testing the diagnostics endpoint
 * @returns {Promise<Object>} Diagnostics result
 */
export async function checkBrandingAuth() {
  try {
    const response = await authFetch('/branding/diagnostics');
    return response.json();
  } catch (error) {
    return {
      authenticated: false,
      error: error.message,
    };
  }
}

export default {
  getAuthToken,
  createAuthHeaders,
  authFetch,
  authFetchJson,
  authUploadFile,
  checkBrandingAuth,
};
